/**
 * Remote inbox HTTP client (`/api/v1/agent/inbox/*`).
 */
import { getBearerAuthHeaders } from "@/lib/authSession";
import { fetchWithAuthRetry } from "@/lib/authedRequest";
import { fetchWithTimeout } from "@/lib/fetchWithTimeout";
import { humanAgentInboxUrl, inboxWorkspaceHeaders, isHumanAgentInboxLive } from "@/lib/inboxLiveConfig";

export {
  inboxListPollIntervalMs,
  isHumanAgentInboxLive,
  setInboxAgentWorkspaceId,
} from "@/lib/inboxLiveConfig";

const DEFAULT_AVATAR =
  "data:image/svg+xml," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect fill="#e2e8f0" width="64" height="64"/><text x="32" y="38" text-anchor="middle" font-size="22" fill="#64748b" font-family="system-ui,sans-serif">?</text></svg>',
  );

/**
 * @param {unknown} row
 * @returns {Record<string, unknown>}
 */
function normalizeConversationRow(row) {
  if (!row || typeof row !== "object") return {};
  const r = /** @type {Record<string, unknown>} */ (row);
  const user = r.user && typeof r.user === "object" ? { ...r.user } : {};
  if (!user.avatar) user.avatar = DEFAULT_AVATAR;
  const slaMeta = r.slaMeta ?? r.sla_meta;
  const base = {
    ...r,
    id: String(r.id ?? ""),
    visitorSessionId: String(r.visitorSessionId ?? r.visitor_session_id ?? r.id ?? ""),
    identityEmails: Array.isArray(r.identityEmails) ? r.identityEmails : Array.isArray(r.identity_emails) ? r.identity_emails : [],
    workspaceId: String(r.workspaceId ?? r.workspace_id ?? ""),
    workspaceName: String(r.workspaceName ?? r.workspace_name ?? ""),
    user: {
      name: String(user.name ?? "Visitor"),
      email: String(user.email ?? ""),
      avatar: String(user.avatar ?? DEFAULT_AVATAR),
      online: Boolean(user.online),
      company: String(user.company ?? ""),
      location: String(user.location ?? ""),
      type: String(user.type ?? "User"),
    },
    preview: String(r.preview ?? ""),
    timestamp: String(r.timestamp ?? ""),
    unread: typeof r.unread === "number" ? r.unread : 0,
    status: String(r.status ?? "open"),
    assignee: String(r.assignee ?? "Unassigned"),
    suggestedAssignee: (() => {
      const raw = r.suggestedAssignee ?? r.suggested_assignee;
      if (raw == null || raw === "") return null;
      return String(raw);
    })(),
    team: String(r.team ?? "Support"),
    tags: Array.isArray(r.tags) ? r.tags : [],
    messages: Array.isArray(r.messages) ? r.messages : [],
  };
  if (slaMeta && typeof slaMeta === "object") {
    base.slaMeta = slaMeta;
  }
  return base;
}

function resolveInboxRequestUrl(path) {
  const s = humanAgentInboxUrl(path);
  if (/^https?:\/\//i.test(s)) return new URL(s);
  return new URL(s, (import.meta.env?.VITE_API_BASE_URL || window.location.origin));
}

async function inboxFetch(
  path,
  { workspaceId, searchParams, timeoutMs, extraHeaders, signal } = {},
) {
  const buildRequest = () => {
    const url = resolveInboxRequestUrl(path);
    if (searchParams && typeof searchParams === "object") {
      for (const [k, v] of Object.entries(searchParams)) {
        if (v === undefined || v === null || v === "") continue;
        url.searchParams.set(k, String(v));
      }
    }
    const init = {
      method: "GET",
      credentials: "include",
      signal,
      headers: {
        Accept: "application/json",
        ...getBearerAuthHeaders(),
        ...inboxWorkspaceHeaders(workspaceId),
        ...(extraHeaders && typeof extraHeaders === "object" ? extraHeaders : {}),
      },
    };
    if (timeoutMs) {
      return fetchWithTimeout(url.toString(), init, timeoutMs);
    }
    return fetch(url.toString(), init);
  };
  const res = await fetchWithAuthRetry(buildRequest, { signal });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = await res.json();
      if (j?.detail) detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
    } catch {
      /* ignore */
    }
    const err = new Error(detail || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/**
 * @param {string} path
 * @param {{ workspaceId?: string | null, body: Record<string, unknown> }} opts
 */
async function inboxJsonResponse(res) {
  if (res.status === 204) return null;
  return res.json();
}

async function inboxMutate(method, path, { workspaceId, body } = {}) {
  const buildRequest = () =>
    fetch(resolveInboxRequestUrl(path).toString(), {
      method,
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
        ...getBearerAuthHeaders(),
        ...inboxWorkspaceHeaders(workspaceId),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  const res = await fetchWithAuthRetry(buildRequest);
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = await res.json();
      if (j?.detail) detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
      else if (j?.message) detail = j.message;
      else if (j?.error) detail = String(j.error);
    } catch {
      /* ignore */
    }
    const err = new Error(detail || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return inboxJsonResponse(res);
}

async function inboxPatch(path, { workspaceId, body }) {
  return inboxMutate("PATCH", path, { workspaceId, body });
}

async function inboxPost(path, { workspaceId, body }) {
  return inboxMutate("POST", path, { workspaceId, body });
}

async function inboxDelete(path, { workspaceId }) {
  return inboxMutate("DELETE", path, { workspaceId });
}

function normalizeTeamInboxRow(row) {
  if (!row || typeof row !== "object") return null;
  const r = /** @type {Record<string, unknown>} */ (row);
  return {
    id: String(r.id ?? ""),
    workspaceId: String(r.workspaceId ?? r.workspace_id ?? ""),
    name: String(r.name ?? ""),
    slug: String(r.slug ?? ""),
    icon: String(r.icon ?? "ri-folder-line"),
    color: r.color != null ? String(r.color) : null,
    sortOrder: typeof r.sortOrder === "number" ? r.sortOrder : Number(r.sort_order) || 0,
    count: typeof r.count === "number" ? r.count : Number(r.count) || 0,
  };
}

function normalizeTeamInboxNav(t) {
  const name = t.name != null ? String(t.name) : undefined;
  return {
    id: String(t.id ?? ""),
    label: String(t.label ?? ""),
    name,
    icon: String(t.icon ?? "ri-folder-line"),
    count: typeof t.count === "number" ? t.count : Number(t.count) || 0,
    color: t.color != null ? String(t.color) : undefined,
    workspaceId: t.workspaceId != null ? String(t.workspaceId) : t.workspace_id != null ? String(t.workspace_id) : undefined,
    workspaceName:
      t.workspaceName != null ? String(t.workspaceName) : t.workspace_name != null ? String(t.workspace_name) : undefined,
    sortOrder: typeof t.sortOrder === "number" ? t.sortOrder : t.sort_order != null ? Number(t.sort_order) : undefined,
  };
}

/**
 * Move the conversation into a team folder or clear folder (teamInboxId null).
 * @param {{ sessionId: string, teamInboxId: string | null, workspaceId: string | null }} params
 */
export async function patchSessionTeamInbox(params) {
  const sid = encodeURIComponent(String(params.sessionId ?? "").trim());
  if (!sid) throw new Error("session id required");
  if (!params.workspaceId || !String(params.workspaceId).trim()) {
    throw new Error("workspace id required to move team inbox");
  }
  await inboxPatch(`/inbox/conversations/${sid}/team-inbox`, {
    workspaceId: params.workspaceId,
    body: { team_inbox_id: params.teamInboxId },
  });
}

/**
 * Correct the visitor email on the tenant session (and linked tickets).
 * @param {{ sessionId: string, email: string, workspaceId: string | null }} params
 * @returns {Promise<{
 *   sessionId: string,
 *   email: string,
 *   visitorEmail: string,
 *   userIdentifier: string,
 *   identityEmails: string[],
 *   name: string,
 *   ticketsUpdated: number,
 * }>}
 */
export async function patchVisitorEmail(params) {
  const sid = encodeURIComponent(String(params.sessionId ?? "").trim());
  if (!sid) throw new Error("session id required");
  const wid = params.workspaceId != null ? String(params.workspaceId).trim() : "";
  if (!wid) throw new Error("workspace id required to update visitor email");
  const email = String(params.email ?? "").trim();
  if (!email) throw new Error("email required");
  const raw = await inboxPatch(`/inbox/conversations/${sid}/visitor-email`, {
    workspaceId: wid,
    body: { email },
  });
  const r = raw && typeof raw === "object" ? /** @type {Record<string, unknown>} */ (raw) : {};
  return {
    sessionId: String(r.sessionId ?? r.session_id ?? params.sessionId ?? ""),
    email: String(r.email ?? email),
    visitorEmail: String(r.visitorEmail ?? r.visitor_email ?? r.email ?? email),
    userIdentifier: String(r.userIdentifier ?? r.user_identifier ?? ""),
    identityEmails: Array.isArray(r.identityEmails)
      ? r.identityEmails.map(String)
      : Array.isArray(r.identity_emails)
        ? r.identity_emails.map(String)
        : [String(r.email ?? email)],
    name: String(r.name ?? ""),
    ticketsUpdated:
      typeof r.ticketsUpdated === "number"
        ? r.ticketsUpdated
        : typeof r.tickets_updated === "number"
          ? r.tickets_updated
          : 0,
  };
}

/**
 * @param {{ view?: string, workspaceId?: string | null, cursor?: string | null, limit?: number }} params
 */
export async function fetchInboxConversations(params = {}) {
  const view = params.view ?? "all";
  const central = params.scope === "central";
  const data = await inboxFetch("/inbox/conversations", {
    workspaceId: central ? null : (params.workspaceId ?? null),
    searchParams: {
      view,
      scope: central ? "central" : "",
      cursor: params.cursor ?? "",
      limit: params.limit ?? 50,
    },
  });
  const rows = Array.isArray(data?.data) ? data.data : [];
  return {
    data: rows.map(normalizeConversationRow),
    nextCursor: data?.next_cursor ?? data?.nextCursor ?? null,
  };
}

/**
 * Search conversations by message body, visitor email, or name.
 * @param {{ q: string, view?: string, workspaceId?: string | null, scope?: string, cursor?: string | null, limit?: number }} params
 */
export async function fetchInboxSearchConversations(params = {}) {
  const q = String(params.q ?? "").trim();
  if (q.length < 2) {
    return { data: [], nextCursor: null };
  }
  const view = params.view ?? "all";
  const central = params.scope === "central";
  const data = await inboxFetch("/inbox/conversations/search", {
    workspaceId: central ? null : (params.workspaceId ?? null),
    searchParams: {
      q,
      view,
      scope: central ? "central" : "",
      cursor: params.cursor ?? "",
      limit: params.limit ?? 50,
    },
  });
  const rows = Array.isArray(data?.data) ? data.data : [];
  return {
    data: rows.map(normalizeConversationRow),
    nextCursor: data?.next_cursor ?? data?.nextCursor ?? null,
  };
}

/**
 * @param {{ workspaceId?: string | null }} [params]
 */
export async function fetchInboxNavigation(params = {}) {
  const central = params.scope === "central";
  const raw = await inboxFetch("/inbox/navigation", {
    workspaceId: central ? null : (params.workspaceId ?? null),
    searchParams: central ? { scope: "central" } : undefined,
  });
  const payload = raw?.data ?? raw;
  const inboxCategories = Array.isArray(payload?.inboxCategories)
    ? payload.inboxCategories
    : Array.isArray(payload?.inbox_categories)
      ? payload.inbox_categories
      : [];
  const teamInboxes = Array.isArray(payload?.teamInboxes)
    ? payload.teamInboxes
    : Array.isArray(payload?.team_inboxes)
      ? payload.team_inboxes
      : [];
  return {
    data: {
      inboxCategories: inboxCategories.map((c) => ({
        id: String(c.id ?? ""),
        label: String(c.label ?? ""),
        icon: String(c.icon ?? "ri-inbox-line"),
        count: typeof c.count === "number" ? c.count : Number(c.count) || 0,
      })),
      teamInboxes: teamInboxes.map((t) => normalizeTeamInboxNav(t)),
    },
  };
}

/**
 * @param {{ workspaceId: string }} params
 */
export async function fetchTeamInboxes(params) {
  const wid = String(params.workspaceId ?? "").trim();
  if (!wid) throw new Error("workspace id required");
  const data = await inboxFetch("/inbox/team-inboxes", { workspaceId: wid });
  const rows = Array.isArray(data?.data) ? data.data : [];
  return { data: rows.map((r) => normalizeTeamInboxRow(r)).filter(Boolean) };
}

/**
 * @param {{ workspaceId: string, body: { name: string, slug?: string, icon?: string, color?: string, sort_order?: number } }} params
 */
export async function createTeamInbox(params) {
  const wid = String(params.workspaceId ?? "").trim();
  if (!wid) throw new Error("workspace id required");
  const row = await inboxPost("/inbox/team-inboxes", { workspaceId: wid, body: params.body });
  return { data: normalizeTeamInboxRow(row) };
}

/**
 * @param {{ workspaceId: string, inboxId: string, body: Record<string, unknown> }} params
 */
export async function updateTeamInbox(params) {
  const wid = String(params.workspaceId ?? "").trim();
  const iid = encodeURIComponent(String(params.inboxId ?? "").trim());
  if (!wid || !iid) throw new Error("workspace id and inbox id required");
  const row = await inboxPatch(`/inbox/team-inboxes/${iid}`, { workspaceId: wid, body: params.body });
  return { data: normalizeTeamInboxRow(row) };
}

/**
 * @param {{ workspaceId: string, inboxId: string }} params
 */
export async function archiveTeamInbox(params) {
  const wid = String(params.workspaceId ?? "").trim();
  const iid = encodeURIComponent(String(params.inboxId ?? "").trim());
  if (!wid || !iid) throw new Error("workspace id and inbox id required");
  await inboxDelete(`/inbox/team-inboxes/${iid}`, { workspaceId: wid });
}

/**
 * @param {{ workspaceId?: string | null }} [params]
 */
export async function fetchInboxDashboard(params = {}) {
  return inboxFetch("/inbox/dashboard", { workspaceId: params.workspaceId ?? null });
}

/**
 * @param {{ id: string, workspaceId?: string | null }} params
 * @returns {Promise<{ data: Record<string, unknown> | null }>}
 */
export async function fetchInboxConversationDetail(params) {
  const sid = encodeURIComponent(String(params.id ?? "").trim());
  if (!sid) throw new Error("conversation id required");
  const central = params.scope === "central";
  const sessionWs =
    params.sessionWorkspaceId && String(params.sessionWorkspaceId).trim()
      ? String(params.sessionWorkspaceId).trim()
      : null;
  const raw = await inboxFetch(`/inbox/conversations/${sid}`, {
    workspaceId: central ? sessionWs : (params.workspaceId ?? null),
    searchParams: central ? { scope: "central" } : undefined,
    signal: params.signal,
  });
  const row = raw?.data ?? null;
  if (!row) return { data: null };
  return { data: normalizeConversationRow(row) };
}

/**
 * Lightweight poll for new customer messages in active handoff sessions.
 * Cursor is sent as X-Inbox-Since-Ms so the poll URL stays stable for CORS.
 *
 * `scope: "central"` reports events from every workspace the agent may read, so the
 * poll covers the same conversations the central inbox list shows.
 *
 * @param {{ sinceMs?: number | null, workspaceId?: string | null, scope?: string | null }} [params]
 * @returns {Promise<{ messages: Array<{session_id: string, message_id: string, message_content: string, created_at: number, workspace_id: string|null}>, handoffs: Array<{session_id: string, handoff_id: number, reason_type: string|null, created_at: number, workspace_id: string|null}>, server_time_ms: number }>}
 */
export async function fetchInboxPoll({ sinceMs = null, workspaceId = null, scope = null } = {}) {
  const extraHeaders = {};
  if (sinceMs != null) extraHeaders["X-Inbox-Since-Ms"] = String(sinceMs);
  return inboxFetch("/inbox/poll", {
    workspaceId,
    searchParams: { scope },
    extraHeaders,
    timeoutMs: 8000,
  });
}

/**
 * Stop bot replies for a bot-only session and assign the conversation to the
 * requesting human agent. The workspace header is mandatory and is verified
 * against the selected session by the backend.
 *
 * @param {{ sessionId: string, workspaceId: string }} params
 */
export async function takeOverBotConversation({ sessionId, workspaceId }) {
  const sid = String(sessionId || "").trim();
  const wid = String(workspaceId || "").trim();
  if (!sid) throw new Error("sessionId is required");
  if (!wid) throw new Error("workspaceId is required");
  return inboxMutate(
    "POST",
    `/inbox/conversations/${encodeURIComponent(sid)}/takeover`,
    { workspaceId: wid },
  );
}
